import React, {useContext, useEffect, useMemo, useState} from "react";
import styles from "./Dashboard.module.scss";
import {HeaderMenu} from "../../common/HeaderMenu";
import {useDispatch, useSelector} from "react-redux";
import {rxShowTransactionEditor, selectAllTransactions} from "../../slices/transactionsSlice";
import moment from "moment";
import {MODE_CREATE, PORTFOLIO_TRANSACTION_TYPE_BUY, TRANSACTION_TYPE_TRANSFER} from "../../codes";
import useFormat from "../../util/useFormat";
import ColoredAmount from "../../common/ColoredAmount";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {rxShowAccountEditor, selectAllAccounts} from "../../slices/accountsSlice";
import useProject from "../../util/useProject";
import {Link, useNavigate} from "react-router";
import {
  appConfigSelector,
  rxCompleteAssistant,
  rxToggleCarsFeature,
  rxTogglePortfolioFeature
} from "../../slices/appSlice";
import useRate from "../../util/useRate";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import {BarChart, LineChart} from "echarts/charts";
import {GridComponent, TitleComponent, TooltipComponent} from "echarts/components";
import {CanvasRenderer,} from "echarts/renderers";
import {listToMap, round} from "../../util/util";
import {Content, ContentFixed, ContentScroll} from "../../common/Content";
import {getPortfolioHistory} from "../../api/ApiPortfolio";
import {rxShowCarEditor, selectAllCars} from "../../slices/carsSlice";
import {rxShowMileageLogEditor, selectAllMileageLogs} from "../../slices/mileageLogsSlice";
import {AppContext} from "../../App";
import Button from "../../common/Button";
import {TransactionEditor} from "../accounts/transaction-editor/TransactionEditor";
import {MileageLogEditor} from "../cars/mileage-log-editor/MileageLogEditor";
import {completeAssistant} from "../../api/ApiApp";
import {RadioField} from "../../common/RadioField";
import {updateSettingsCarsFeature, updateSettingsPortfolioFeature} from "../../api/ApiSettings";
import {BudgetBarIn, BudgetBarOut} from "../../common/BudgetBar";
import {PortfolioTransactionEditor} from "../portfolio/portfolio-transaction-editor/PortfolioTransactionEditor";
import {selectAllPortfolioTransactions} from "../../slices/portfolioSlice";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";

echarts.use([TitleComponent, TooltipComponent, GridComponent, LineChart, BarChart, CanvasRenderer]);

const Dashboard = () => {
  const {t} = useTranslation("app");
  const {marketValue, ongoingGains, quotes} = useContext(AppContext).portfolio;

  const dispatch = useDispatch();

  const config = useSelector(appConfigSelector);

  const accounts = useSelector(selectAllAccounts);
  const cars = useSelector(selectAllCars);

  const [portfolioHistory, setPortfolioHistory] = useState([]);

  useEffect(() => {
    if (config.featurePortfolioActive) {
      getPortfolioHistory().then(setPortfolioHistory);
    }
  }, [config.featurePortfolioActive]);

  const showTransactionEditor = () => dispatch(rxShowTransactionEditor({mode: MODE_CREATE, transaction: {}}));
  const showMileageLogEditor = () => dispatch(rxShowMileageLogEditor({mode: MODE_CREATE, mileageLog: {}}));

  return (<Content>
    <ContentFixed>
      <HeaderMenu title={t("header_title_dashboard")}>
        <div className={styles.actions}>
          {accounts.length > 0 &&
            <button type="button"
                    className={clsx(styles.actionButton, styles.accountTransaction)}
                    onClick={showTransactionEditor}>
              <span className={clsx("material-icons", styles.actionButtonPlusIcon)}>add</span>
              <span className={clsx("material-icons", styles.actionButtonIcon)}>account_balance</span>
              {t("dashboard_action_add_account_transaction")}
            </button>
          }

          {config.featureCarsActive && cars.length > 0 &&
            <button type="button"
                    className={clsx(styles.actionButton, styles.carsMileageLog)}
                    onClick={showMileageLogEditor}>
              <span className={clsx("material-icons", styles.actionButtonPlusIcon)}>add</span>
              <span className={clsx("material-icons", styles.actionButtonIcon)}>local_gas_station</span>
              {t("dashboard_action_add_cars_mileage_log")}
            </button>
          }
        </div>
      </HeaderMenu>
    </ContentFixed>

    <ContentScroll>
      <div className={styles.dashboardGrid}>
        <Project/>

        <Welcome/>

        <Amounts marketValue={marketValue} ongoingGains={ongoingGains}/>

        <Projection portfolioHistory={portfolioHistory} marketValue={marketValue}/>

        <Budget/>

        <Transactions/>

        <AccountsBalanceHistory portfolioHistory={portfolioHistory}/>

        <Quotes quotes={quotes}/>

        <CarConsumptionHistory/>

        <TransactionEditor/>
        <PortfolioTransactionEditor/>
        <MileageLogEditor/>
      </div>
    </ContentScroll>
  </Content>);
};

const Project = () => {
  const {expired, expiredSoon, expirationDate} = useProject();
  const {formatDate} = useFormat();
  const {t} = useTranslation("app");

  if (expired) {
    return <div className={clsx("card", styles.card, styles.projectExpired)}>
      <p><strong>{t("expired_project_card_title")}</strong></p>
      <p>{t("expired_project_card_text", {date: formatDate(expirationDate)})}</p>
      <p>{t("expired_project_card_go_to_settings")}</p>
      <p><Link to="/settings/subscription">{t("expired_project_card_settings_link")}</Link></p>
    </div>;
  } else if (expiredSoon) {
    return <div className={clsx("card", styles.card, styles.projectExpiredSoon)}>
      <p><strong>{t("expired_soon_project_card_title")}</strong></p>
      <p>{t("expired_soon_project_card_text", {date: formatDate(expirationDate)})}</p>
      <p>{t("expired_soon_project_card_go_to_settings")}</p>
      <p><Link to="/settings/subscription">{t("expired_soon_project_card_settings_link")}</Link></p>
    </div>;
  } else {
    return null;
  }
};

const Welcome = () => {
  const {t} = useTranslation("app");

  const dispatch = useDispatch();

  const config = useSelector(appConfigSelector);
  const accounts = useSelector(selectAllAccounts);
  const transactions = useSelector(selectAllTransactions);
  const cars = useSelector(selectAllCars);
  const mileageLogs = useSelector(selectAllMileageLogs);

  const showAccountEditor = () => dispatch(rxShowAccountEditor({mode: MODE_CREATE, account: {}}));
  const showTransactionEditor = () => dispatch(rxShowTransactionEditor({mode: MODE_CREATE, transaction: {}}));
  const showCarEditor = () => dispatch(rxShowCarEditor({mode: MODE_CREATE, car: {}}));
  const showMileageLogEditor = () => dispatch(rxShowMileageLogEditor({mode: MODE_CREATE, mileageLog: {}}));

  const onCompleteAssistant = () => {
    completeAssistant();

    dispatch(rxCompleteAssistant());
  };

  const setPortfolioFeature = value => {
    dispatch(rxTogglePortfolioFeature(value));

    updateSettingsPortfolioFeature({featurePortfolioActive: value});
  };

  const setCarsFeature = value => {
    dispatch(rxToggleCarsFeature(value));

    updateSettingsCarsFeature({featureCarsActive: value});
  };

  const typeChoices = [
    {value: false, label: t("assistant_option_disabled"), activeClass: "success"},
    {value: true, label: t("assistant_option_enabled"), activeClass: "success"}
  ];

  if (!config.assistant) {
    return null;
  }

  return <div className={clsx("card", styles.card, styles.assistantContainer)}>
    <div className={styles.titleInfos}>
      <h3>{t("assistant_title")}</h3>

      <p>{t("assistant_infos")}</p>
    </div>

    <AssistantStep title={t("assistant_add_account_title")} done={accounts.length > 0} onClick={showAccountEditor}>
      {t("assistant_add_account_text")}
    </AssistantStep>

    <AssistantStep title={t("assistant_add_transaction_title")}
                   done={transactions.length > 0}
                   disabled={accounts.length === 0}
                   onClick={showTransactionEditor}>
      {t("assistant_add_transaction_text")}
    </AssistantStep>

    <hr/>

    <AssistantStep title={t("assistant_enable_portfolio_title")}
                   icon="swipe"
                   onClick={() => setPortfolioFeature(!config.featurePortfolioActive)}>
      <>
        <p>
          {t("assistant_enable_portfolio_text1")}
          <br/>
          {t("assistant_enable_portfolio_text2")}
        </p>

        <div>
          <RadioField
            value={config.featurePortfolioActive}
            choices={typeChoices}
            onChange={setPortfolioFeature}
          />
        </div>
      </>
    </AssistantStep>

    <hr/>

    <AssistantStep title={t("assistant_enable_cars_title")}
                   icon="swipe"
                   onClick={() => setCarsFeature(!config.featureCarsActive)}>
      <>
        <p>
          {t("assistant_enable_cars_text1")}
          <br/>
          {t("assistant_enable_cars_text2")}
          <br/>
        </p>

        <div>
          <RadioField
            value={config.featureCarsActive}
            choices={typeChoices}
            onChange={setCarsFeature}
          />
        </div>
      </>
    </AssistantStep>

    <AssistantStep title={t("assistant_add_car_title")}
                   done={cars.length > 0}
                   disabled={!config.featureCarsActive}
                   onClick={showCarEditor}>
      {t("assistant_add_car_text")}
    </AssistantStep>

    <AssistantStep title={t("assistant_add_mileage_log_title")}
                   done={mileageLogs.length > 0}
                   disabled={cars.length === 0}
                   onClick={showMileageLogEditor}>
      {t("assistant_add_mileage_log_text")}
    </AssistantStep>

    <hr/>

    <div className={styles.closeAssistant}>
      <Button onClick={onCompleteAssistant} variant="secondary">{t("assistant_close_button")}</Button>
    </div>
  </div>;
};

const AssistantStep = ({title, done, disabled, onClick, icon, children}) => {
  let finalIcon;

  if (done) {
    finalIcon = "check_circle";
  } else if (icon) {
    finalIcon = icon;
  } else {
    finalIcon = "radio_button_unchecked";
  }

  return <div
    className={clsx(styles.assistantStep, disabled ? styles.disabled : "hover-selectable", done ? styles.done : null)}
    onClick={disabled ? null : onClick}>
    <div className={styles.doneNotDone}>
      <span className="material-icons">{finalIcon}</span>
    </div>
    <div className={clsx(styles.details)}>
      <div className={styles.title}>{title}</div>
      <div className={styles.description}>{children}</div>
    </div>
  </div>;
};

const Actions = () => {
  const {t} = useTranslation("app");

  const dispatch = useDispatch();

  const config = useSelector(appConfigSelector);

  const accounts = useSelector(selectAllAccounts);
  const cars = useSelector(selectAllCars);

  const showTransactionEditor = () => dispatch(rxShowTransactionEditor({mode: MODE_CREATE, transaction: {}}));

  const showMileageLogEditor = () => dispatch(rxShowMileageLogEditor({mode: MODE_CREATE, mileageLog: {}}));

  if (config.assistant) {
    return null;
  }

  return <div className={styles.actions}>
    {accounts.length > 0 &&
      <button type="button"
              className={clsx(styles.actionButton, styles.accountTransaction)}
              onClick={showTransactionEditor}>
        <span className={clsx("material-icons", styles.actionButtonPlusIcon)}>add</span>
        <span className={clsx("material-icons", styles.actionButtonIcon)}>account_balance</span>
        {t("dashboard_action_add_account_transaction")}
      </button>
    }

    {config.featureCarsActive && cars.length > 0 &&
      <button type="button"
              className={clsx(styles.actionButton, styles.carsMileageLog)}
              onClick={showMileageLogEditor}>
        <span className={clsx("material-icons", styles.actionButtonPlusIcon)}>add</span>
        <span className={clsx("material-icons", styles.actionButtonIcon)}>local_gas_station</span>
        {t("dashboard_action_add_cars_mileage_log")}
      </button>
    }
  </div>;
};

const Amounts = ({marketValue, ongoingGains}) => {
  const {formatAmount} = useFormat();
  const {getPrice} = useRate();
  const {currencyMain, portfolioCurrency, featurePortfolioActive} = useSelector(appConfigSelector);

  const {quotes} = useContext(AppContext).portfolio;

  const {t} = useTranslation("app");

  const accounts = useSelector(selectAllAccounts);
  const activeAccounts = accounts.filter(a => a.active);
  const accountsValue = activeAccounts
    .map(a => getPrice(a.balance, a.currency, currencyMain))
    .reduce((a, b) => a + b, 0);

  let total = accountsValue;

  if (featurePortfolioActive) {
    total += getPrice(marketValue, portfolioCurrency, currencyMain);
  }

  const activeQuotes = Object.values(quotes).filter(t => t.quantity > 0);

  return <div className={clsx("card", styles.card)}>
    <div className={clsx(styles.amounts)}>
      <div className={styles.amount}>
        <div className={styles.amountLabel}>{t("dashboard_amount_total")}</div>
        <div className={styles.amountValue}>{formatAmount(total)}</div>
      </div>

      <div className={styles.amount}>
        <div className={styles.amountLabel}>{t("dashboard_amount_accounts")}</div>
        <div className={styles.amountValue}>{formatAmount(accountsValue)}</div>
      </div>

      {featurePortfolioActive && <div className={styles.amount}>
        <div className={styles.amountLabel}>{t("dashboard_amount_portfolio")}</div>
        <div className={styles.amountValue}>{formatAmount(marketValue, portfolioCurrency)}</div>
      </div>}
    </div>

    {(activeAccounts.length > 0 || (featurePortfolioActive && activeQuotes.length > 0)) &&
      <div className={clsx(styles.accounts)}>
        {activeAccounts.map(account => {
          const [background] = account.color.split("|");

          return <div className={clsx(styles.account)} key={account.id}>
            <div className={styles.accountColor} style={{background}}/>
            <div className={styles.accountBalance}>{formatAmount(account.balance, account.currency)}</div>
            <div className={styles.accountName}>
              {account.name}

              <span className={styles.accountDescription}>{account.description}</span>
            </div>
          </div>;
        })}

        {activeAccounts.length > 0 && <div className={clsx(styles.account, styles.total)}>
          <div className={styles.accountColor}/>
          <div className={styles.accountBalance}>{formatAmount(accountsValue)}</div>
          <div className={styles.accountName}>{t("dashboard_amount_total_accounts")}</div>
        </div>}

        {featurePortfolioActive && activeQuotes.length > 0 && <>
          {activeQuotes
            .toSorted((a, b) => b.marketValue - a.marketValue)
            .map(quote => {
              return <div className={clsx(styles.account)} key={quote.symbol}>
                <div
                  className={clsx(styles.accountColor, quote.ongoingGains >= 0 ? styles.positive : styles.negative)}
                />
                <div className={styles.accountBalance}>
                  {formatAmount(quote.marketValue, portfolioCurrency)}
                </div>
                <div className={styles.accountName}>
                  {quote.symbol}

                  <span className={styles.accountDescription}>{quote.name}</span>
                </div>
                <div className={styles.variation}>
                  <ColoredAmount amount={quote.ongoingGains}>
                    {(quote.ongoingGains >= 0 ? "+" : "") + formatAmount(quote.ongoingGains, portfolioCurrency)}
                  </ColoredAmount>
                </div>
              </div>;
            })}

          <div className={clsx(styles.account, styles.total)}>
            <div className={clsx(styles.accountColor, ongoingGains >= 0 ? styles.positive : styles.negative)}/>
            <div className={styles.accountBalance}>{formatAmount(marketValue, portfolioCurrency)}</div>
            <div className={styles.accountName}>{t("dashboard_amount_total_portfolio")}</div>
            <div className={styles.variation}>
              <ColoredAmount amount={ongoingGains}>
                {(ongoingGains >= 0 ? "+" : "") + formatAmount(ongoingGains, portfolioCurrency)}
              </ColoredAmount>
            </div>
          </div>
        </>}
      </div>}
  </div>;
};

const Projection = ({portfolioHistory, marketValue}) => {
  const {formatAmount, formatDate} = useFormat();
  const {getPrice} = useRate();
  const {t} = useTranslation("app");

  const {currencyMain} = useSelector(appConfigSelector);

  const [monthlySaving, setMonthlySaving] = useState(500);
  const [monthlyInvestment, setMonthlyInvestment] = useState(3000);

  const accounts = useSelector(selectAllAccounts);
  const allTransactions = useSelector(selectAllTransactions);

  let monthlyMarketValue = {};
  let monthlyAccountsValue = {};

  let accountsValue = accounts.filter(a => a.active)
    .map(a => getPrice(a.balance, a.currency, currencyMain))
    .reduce((a, b) => a + b, 0);

  const now = moment();
  const monthlyDate = 1;
  const currentDate = now.date();

  const projections = [];

  const projectionStart = now.clone().startOf('month').date(monthlyDate);

  if (currentDate < monthlyDate) {
    projectionStart.subtract(1, "month");
  }

  const projectionStartFormatted = projectionStart.format("YYYY-MM-DD");

  const transactionsDelta = useMemo(() => {
    return allTransactions
      .filter(t => !t.future && t.type !== TRANSACTION_TYPE_TRANSFER && t.valueDate.localeCompare(projectionStartFormatted) >= 0)
      .map(t => round(t.amount * t.rate, 2))
      .reduce((a, b) => a + b, 0);
  }, [projectionStartFormatted, allTransactions]);

  let projectStartMarketValue = 0;

  for (let portfolioIndex = 0; portfolioIndex < portfolioHistory.length; portfolioIndex++) {
    if (moment(portfolioHistory[portfolioIndex].valueDate).isSameOrAfter(projectionStart)) {
      continue;
    }

    projectStartMarketValue = portfolioHistory[portfolioIndex].value;

    break;
  }

  let ratesStart = {};

  for (let i = 6; i < 11; i++) {
    monthlyMarketValue[i] = projectStartMarketValue;
    monthlyAccountsValue[i] = accountsValue - transactionsDelta;

    ratesStart[i] = monthlyAccountsValue[i] + monthlyMarketValue[i];
  }

  projections.push({date: formatDate(projectionStart.clone()), rates: ratesStart});

  if (currentDate !== monthlyDate) {
    let ratesNow = {};

    for (let i = 6; i < 11; i++) {
      ratesNow[i] = accountsValue + marketValue;
    }

    projections.push({date: formatDate(now.clone()), rates: ratesNow});
  }

  const currentMonth = now.month();

  let end = (2046 - moment().year()) * 12;
  let nextMonthAdded = false;

  for (let i = 1; i <= end; i++) {
    let rates = {};

    for (let j = 6; j < 11; j++) {
      const monthlyRate = j / 12 / 100;

      monthlyAccountsValue[j] += monthlySaving;
      monthlyMarketValue[j] += (monthlyInvestment + (monthlyMarketValue[j] * monthlyRate));

      rates[j] = monthlyAccountsValue[j] + monthlyMarketValue[j];
    }

    projectionStart.add(1, "month");

    if ((i + currentMonth) % 12 === 0 || !nextMonthAdded) {
      projections.push({date: formatDate(projectionStart.clone()), rates});

      nextMonthAdded = true;
    }
  }

  return <div className={clsx("card", styles.card)}>
    <h5 className={styles.cardTitle}>{t("projections_title")}</h5>

    <div className={styles.projectionConfig}>
      <div className={styles.projectionParameters}>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column={true} sm={3}>{t("projections_monthly_savings")}</Form.Label>
          <Col sm={3}>
            <Form.Control type="number" value={monthlySaving} onChange={e => setMonthlySaving(+e.target.value)}/>
          </Col>
          <Form.Label column={true} sm={3}>{t("projections_monthly_investment")}</Form.Label>
          <Col sm={3}>
            <Form.Control type="number" value={monthlyInvestment}
                          onChange={e => setMonthlyInvestment(+e.target.value)}/>
          </Col>
        </Form.Group>
      </div>
    </div>

    <div className={styles.projectionList}>
      <table>
        <tbody>
        <tr className={clsx(styles.projection, styles.header)}>
          <td className={styles.projectionDate}>{t("projections_value_date")}</td>

          {Object.keys(projections[0].rates).map(rate => <td key={rate}
                                                             className={styles.projectionValue}>{rate}%</td>)}
        </tr>

        {projections.map(projection => {
          return <tr key={projection.date} className={clsx(styles.projection)}>
            <td className={styles.projectionDate}>{projection.date}</td>

            {Object.keys(projection.rates).map(rate => <td key={rate} className={styles.projectionValue}>
              {formatAmount(projection.rates[rate])}
            </td>)}
          </tr>;
        })}
        </tbody>
      </table>
    </div>
  </div>;
}

const Budget = () => {
  const {t} = useTranslation("app");

  const {formatAmount} = useFormat();

  const {periods} = useContext(AppContext).budget;

  const now = moment();
  const currentYear = now.year();
  const currentMonth = now.month();
  const currentPeriod = periods.find(p => p.year === currentYear && p.month === currentMonth);

  return <div className={clsx("card", styles.card)}>
    <div className={styles.budgetBars}>
      <h5 className={styles.cardTitle}>
        {t("dashboard_budget_title", {
          date: moment({
            year: currentPeriod.year,
            month: currentPeriod.month,
            day: 1
          }).format("MMMM YYYY")
        })}
      </h5>

      <div className={styles.budgetBarTitle}>
        <div className={styles.title}>{t("dashboard_budget_bar_title_in")}</div>
        <div className={styles.numbers}>
          {formatAmount(Math.abs(currentPeriod.amountInFilled))} / {formatAmount(Math.abs(currentPeriod.amountInPlanned))}
        </div>
      </div>
      <div className={styles.budgetBar}>
        <BudgetBarIn amountFilled={currentPeriod.amountInFilled} amountPlanned={currentPeriod.amountInPlanned}/>
      </div>

      <div className={styles.budgetBarTitle}>
        <div className={styles.title}>{t("dashboard_budget_bar_title_out")}</div>
        <div className={styles.numbers}>
          {formatAmount(Math.abs(currentPeriod.amountOutFilled))} / {formatAmount(Math.abs(currentPeriod.amountOutPlanned))}
        </div>
      </div>
      <div className={styles.budgetBar}>
        <BudgetBarOut amountFilled={currentPeriod.amountOutFilled} amountPlanned={currentPeriod.amountOutPlanned}/>
      </div>
    </div>
  </div>;
};

const Transactions = () => {
  const {t} = useTranslation("app");

  const {formatAmount, formatNumber, formatDate} = useFormat();

  const allTransactions = useSelector(selectAllTransactions);
  const accountsList = useSelector(selectAllAccounts);
  const carsList = useSelector(selectAllCars);
  const mileageLogsList = useSelector(selectAllMileageLogs);
  const portfolioTransactionsList = useSelector(selectAllPortfolioTransactions);

  const accounts = useMemo(() => listToMap(accountsList, "id"), [accountsList]);
  const cars = useMemo(() => listToMap(carsList, "id"), [carsList]);

  const mileageLogs = useMemo(() => listToMap(mileageLogsList.filter(ml => ml.transaction), "transaction"), [mileageLogsList]);
  const portfolioTransactions = useMemo(() => listToMap(portfolioTransactionsList.filter(sp => sp.transaction), "transaction"), [portfolioTransactionsList]);

  const lastTransactions = allTransactions.filter(t => t.type !== TRANSACTION_TYPE_TRANSFER && !t.future).slice(0, 10);

  if (lastTransactions.length === 0) {
    return null;
  }

  return <div className={clsx("card", styles.card)}>
    <h5 className={styles.cardTitle}>{t("dashboard_transactions_title")}</h5>

    <div className={styles.transactions}>
      {lastTransactions.map(transaction => {
        const account = accounts[transaction.account];

        let payeeComponent = <span>{transaction.payeeDisplay || transaction.payee}</span>;

        let note = transaction.note;
        let mileageLog = mileageLogs[transaction.id];
        let portfolioTransaction = portfolioTransactions[transaction.id];

        if (mileageLog) {
          const car = cars[mileageLog.car];

          const [background, color] = car.color.split("|");

          payeeComponent = <span className={clsx(styles.tag)} style={{background, color}}>{car.name}</span>;

          const parts = [];

          if (mileageLog.distance) {
            parts.push(formatNumber(mileageLog.odometer, "#T##0D##") + " " + car.odometerUnit + " (+" + formatNumber(mileageLog.distance, "#T##0D##") + " " + car.odometerUnit + ")");
          } else {
            parts.push(formatNumber(mileageLog.odometer, "#T##0D##") + " " + car.odometerUnit);
          }

          parts.push(formatNumber(mileageLog.quantity) + " " + car.quantityUnit + " @ " + formatAmount(mileageLog.price));

          if (mileageLog.conso) {
            parts.push(formatNumber(mileageLog.conso, "#T##0D000") + " " + car.consoUnit);
          }

          note = parts.join(" | ");
        } else if (portfolioTransaction) {
          const positiveNegativeStyle = portfolioTransaction.type === PORTFOLIO_TRANSACTION_TYPE_BUY
            ? styles.portfolioTransactionPositive
            : styles.portfolioTransactionNegative;

          payeeComponent = <span className={clsx(styles.tag, positiveNegativeStyle)}>
          {portfolioTransaction.type === PORTFOLIO_TRANSACTION_TYPE_BUY ? "BUY" : "SELL"} {portfolioTransaction.symbol}
        </span>;

          note = formatNumber(portfolioTransaction.quantity, "#T##0D##") + " @ " + formatAmount(portfolioTransaction.price)
            + (portfolioTransaction.note ? " | " + portfolioTransaction.note : "");
        }

        return <div key={transaction.id} className={styles.transaction}>
          <div className={styles.name}>
            {payeeComponent}

            {note && <span className={styles.note}>{note}</span>}
          </div>
          <div className={styles.amount}>
            <ColoredAmount className={styles.amount} amount={transaction.amount}>
              {formatAmount(transaction.amount, account.currency)}
            </ColoredAmount>
          </div>
          <div className={styles.valueDate}>
            {formatDate(transaction.valueDate)}
          </div>
        </div>;
      })}
    </div>
  </div>;
};

const AccountsBalanceHistory = ({portfolioHistory = []}) => {
  const {formatAmount, formatNumber} = useFormat();
  const {getPrice} = useRate();

  const {t} = useTranslation("app");

  const {currencyMain, portfolioCurrency} = useSelector(appConfigSelector);
  const allAccounts = useSelector(selectAllAccounts);
  const allTransactions = useSelector(selectAllTransactions);

  const [granularity, setGranularity] = useState("year");
  const [chartAccounts, setChartAccounts] = useState(true);
  const [chartPortfolio, setChartPortfolio] = useState(true);

  const {featurePortfolioActive} = useSelector(appConfigSelector);

  const portfolioTransactionsList = useSelector(selectAllPortfolioTransactions);

  const portfolioTransactions = useMemo(() => listToMap(portfolioTransactionsList.filter(sp => sp.transaction), "transaction"), [portfolioTransactionsList]);

  const startDateString = "2020-01-01";
  const keyFormat = granularity === "year" ? "YYYY" : "MMM YY";
  const unit = granularity;

  const startDate = moment(startDateString);
  const startDateFormatted = startDate.format("YYYY-MM-DD");

  const transactions = useMemo(() => {
    return allTransactions.filter(t => !t.future && t.type !== TRANSACTION_TYPE_TRANSFER && t.valueDate.localeCompare(startDateFormatted) >= 0);
  }, [startDateFormatted, allTransactions]);

  const transactionsByDate = useMemo(() => {
    const res = {};

    transactions.forEach(t => {
      const key = moment(t.valueDate).format(keyFormat);

      if (!res[key]) {
        res[key] = 0;
      }

      res[key] += round(t.amount * t.rate, 2);
    });

    return res;
  }, [transactions, keyFormat]);

  const portfolioTransactionsByDate = useMemo(() => {
    const res = {};

    transactions.forEach(t => {
      let portfolioTransaction = portfolioTransactions[t.id];

      if (!portfolioTransaction) {
        return;
      }

      const key = moment(t.valueDate).format(keyFormat);

      if (!res[key]) {
        res[key] = 0;
      }

      res[key] += round(t.amount * t.rate, 2);
    });

    return res;
  }, [portfolioTransactions, transactions, keyFormat]);

  const balanceEvolutionNow = moment().endOf(unit);
  const balanceEvolutionMin = moment(startDateString).endOf(unit);

  let currentBalance = allAccounts
    .filter(a => a.active)
    .map(a => getPrice(a.balance, a.currency, currencyMain))
    .reduce((a, b) => a + b, 0);

  const dates = [];

  while (balanceEvolutionNow.isSameOrAfter(balanceEvolutionMin)) {
    const key = balanceEvolutionNow.format(keyFormat);

    const date = {
      date: balanceEvolutionNow.clone(),
      balanceEvolution: round(currentBalance, 2),
      portfolioEvolution: 0,
      portfolioAddition: 0
    }

    const portfolioValue = portfolioHistory.find(v => v.valueDate <= balanceEvolutionNow.format("YYYY-MM-DD"));

    if (portfolioValue) {
      date.portfolioEvolution = portfolioValue.value;
    }

    currentBalance -= transactionsByDate[key] || 0;

    balanceEvolutionNow.subtract(1, unit).endOf(unit);

    if (portfolioTransactionsByDate[key]) {
      date.portfolioAddition = portfolioTransactionsByDate[key];
    }

    dates.push(date);
  }

  const tooltipPortfolio = t("dashboard_tooltip_portfolio");
  const tooltipAccounts = t("dashboard_tooltip_accounts");
  const tooltipTotal = t("dashboard_tooltip_total");

  const series = [];
  const colors = [];

  let portfolioIndex = 1;

  if (chartAccounts) {
    series.push({
      name: "Comptes",
      type: "line",
      stack: "Total",
      areaStyle: {
        color: "#55555588"
      },
      emphasis: {
        focus: "none"
      },
      data: dates.map(d => d.balanceEvolution).reverse(),
    });

    colors.push("#555555");
  }

  if (featurePortfolioActive && chartPortfolio) {
    series.push({
      name: "Portfolio",
      type: "line",
      stack: "Total",
      areaStyle: {
        color: "#30a14e88"
      },
      emphasis: {
        focus: "none"
      },
      data: dates.map(d => d.portfolioEvolution).reverse()
    });

    colors.push("#30a14e");

    if (!chartAccounts) {
      portfolioIndex = 0;
    }
  }

  return <div className={clsx("card", styles.card)}>
    <div className={styles.cardTitleAccountsBalanceHistory}>
      <h5 className={styles.cardTitle}>{t("dashboard_accounts_balance_history_title")}</h5>

      <div className={styles.cardTitleAccountsBalanceHistoryActions}>
        <button className={clsx(styles.btnGranularity)} onClick={() => setGranularity("year")}>Par année
        </button>
        <button className={clsx(styles.btnGranularity)} onClick={() => setGranularity("month")}>Par mois
        </button>
        |
        <button className={clsx(styles.btnAccounts, !chartAccounts ? styles.btnInactive : null)}
                onClick={() => setChartAccounts(p => !p)}>Comptes
        </button>

        {featurePortfolioActive &&
          <button className={clsx(styles.btnPortfolio, !chartPortfolio ? styles.btnInactive : null)}
                  onClick={() => setChartPortfolio(p => !p)}>Portfolio</button>}
      </div>
    </div>

    <ReactEChartsCore
      echarts={echarts}
      option={{
        grid: {
          top: 20,
          right: 20,
          bottom: 30,
          left: 20
        },
        color: colors,
        animation: false,
        tooltip: {
          trigger: "axis",
          formatter: params => {
            let total = 0;

            let res = "<div class=\"echart-tooltip\">" +
              "<p class=\"echart-tooltip-title\">" + params[0].name + "</p>";

            if (featurePortfolioActive && chartPortfolio) {
              let portfolioAmount = params.length > portfolioIndex ? params[portfolioIndex].data : 0;

              total += portfolioAmount;

              res += "<div style=\"display:flex;\">" +
                "<div style=\"flex:1;margin-right:15px;\">" + tooltipPortfolio + "</div>" +
                "<strong style=\"justify-content:flex-end;\">" + formatAmount(portfolioAmount, portfolioCurrency) + "</strong>" +
                "</div>";
            }

            if (chartAccounts) {
              total += params[0].data;

              res += "<div style=\"display:flex;\">" +
                "<div style=\"flex:1;margin-right:15px;\">" + tooltipAccounts + "</div>" +
                "<strong style=\"justify-content:flex-end;\">" + formatAmount(params[0].data) + "</strong>" +
                "</div>";
            }

            res += "<div style=\"display:flex;border-top:1px solid #ddd;margin-top:3px;padding-top:3px;\">" +
              "<div style=\"flex:1;margin-right:15px;\">" + tooltipTotal + "</div>" +
              "<strong style=\"justify-content:flex-end;\">" + formatAmount(total) + "</strong>" +
              "</div>" +
              "</div>";

            return res;
          }
        },
        xAxis: {
          type: "category",
          data: dates.map(d => d.date.format(keyFormat)).reverse()
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter: val => formatAmount(val),
            inside: true,
            showMinLabel: false
          }
        },
        series
      }}
      notMerge={true}
      lazyUpdate={true}
      theme={"theme_name"}
    />

    <div className={styles.amountsHistoryList}>
      <table>
        <thead>
        <tr className={clsx(styles.projection, styles.header)}>
          <td className={styles.projectionDate}></td>
          <td>Total</td>
          <td>Comptes</td>
          <td>Portfolio</td>
          <td>Ajout Portfolio</td>
          <td className={styles.projectionSeparator}></td>
          <td>% Portfolio</td>
          <td>% Comptes</td>
          <td>% Total</td>
          <td>€ Total</td>
          <td className={styles.projectionSeparator}></td>
          <td>% Portfolio</td>
          <td>€ Portfolio</td>
        </tr>
        </thead>
        <tbody>
        {dates.map((date, i) => {
          let portfolioDiffPercentage = null;
          let balanceDiffPercentage = null;
          let totalDiffPercentage = null;
          let totalDiffAmount = null;
          let portfolioNetDiffPercentage = null;
          let portfolioNetDiffAmount = null;

          if (dates[i + 1]) {
            const nextDate = dates[i + 1];

            const portfolioDate = date.portfolioEvolution;
            const portfolioPreviousDate = nextDate.portfolioEvolution;
            const balanceDate = date.balanceEvolution;
            const balancePreviousDate = nextDate.balanceEvolution;

            if (portfolioPreviousDate) {
              portfolioDiffPercentage = ((portfolioDate / portfolioPreviousDate) - 1) * 100;
              portfolioNetDiffAmount = (portfolioDate + (date.portfolioAddition || 0)) - portfolioPreviousDate;
              portfolioNetDiffPercentage = (portfolioNetDiffAmount / portfolioDate) * 100;
            }

            balanceDiffPercentage = ((balanceDate / balancePreviousDate) - 1) * 100;
            totalDiffPercentage = (((portfolioDate + balanceDate) / (portfolioPreviousDate + balancePreviousDate)) - 1) * 100;

            totalDiffAmount = (portfolioDate + balanceDate) - (portfolioPreviousDate + balancePreviousDate);
          }

          return <tr key={date.date.format(keyFormat)}
                     className={clsx(styles.projection, date.date.get('month') === 0 ? styles.december : null)}>
            <td className={styles.projectionDate}>{date.date.format(keyFormat)}</td>
            <td
              className={styles.projectionValue}>{formatAmount(date.portfolioEvolution + date.balanceEvolution)}</td>
            <td className={styles.projectionValue}>{formatAmount(date.balanceEvolution)}</td>
            <td className={styles.projectionValue}>{formatAmount(date.portfolioEvolution)}</td>
            <td
              className={styles.projectionValue}>{date.portfolioAddition ? formatAmount(-date.portfolioAddition) : "-"}</td>
            <td className={styles.projectionSeparator}></td>
            <td
              className={styles.projectionValue}>{portfolioDiffPercentage ? formatNumber(portfolioDiffPercentage) : "-"}%
            </td>
            <td className={styles.projectionValue}>{formatNumber(balanceDiffPercentage)}%</td>
            <td className={styles.projectionValue}>{formatNumber(totalDiffPercentage)}%</td>
            <td className={styles.projectionValue}>{formatAmount(totalDiffAmount)}</td>
            <td className={styles.projectionSeparator}></td>
            <td
              className={styles.projectionValue}>{portfolioNetDiffPercentage ? formatNumber(portfolioNetDiffPercentage) : "-"}%
            </td>
            <td className={styles.projectionValue}>{formatAmount(portfolioNetDiffAmount)}</td>
          </tr>;
        })}
        </tbody>
      </table>
    </div>
  </div>;
};

const CarConsumptionHistory = () => {
  const {t} = useTranslation("app");

  const config = useSelector(appConfigSelector);

  const allMileageLogs = useSelector(selectAllMileageLogs);

  const cars = useSelector(selectAllCars);
  const defaultCar = cars.find(c => c.default);

  const {formatNumber, formatDate} = useFormat();

  if (!config.featureCarsActive || !defaultCar) {
    return null;
  }

  const mileageLogs = allMileageLogs
    .filter(ml => ml.car === defaultCar.id)
    .filter(ml => ml.conso);

  if (mileageLogs.length === 0) {
    mileageLogs.push({valueDate: moment().format("YYYY-MM-DD"), conso: 0});
  }

  return <div className={clsx("card", styles.card)}>
    <h5 className={styles.cardTitle}>
      {t("dashboard_car_average_conso_title", {car_name: defaultCar.name})}
    </h5>

    <ReactEChartsCore
      echarts={echarts}
      option={{
        grid: {
          top: 20,
          right: 20,
          bottom: 30,
          left: 20
        },
        color: ["#555", "#30a14e"],
        animation: false,
        tooltip: {
          trigger: "axis",
          formatter: params => {
            return "<div class=\"echart-tooltip\">" +
              "<p class=\"echart-tooltip-title\">" + params[0].name + "</p>" +
              "<div>" + formatNumber(params[0].data, "#T##0D000") + " " + defaultCar.consoUnit + "</div>" +
              "</div>";
          }
        },
        xAxis: {
          type: "category",
          data: mileageLogs.map(v => formatDate(v.valueDate)).reverse(),
          axisLabel: {
            showMinLabel: false
          }
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter: val => formatNumber(val, "#T##0D000") + " " + defaultCar.consoUnit,
            inside: true,
            showMinLabel: false
          }
        },
        series: [
          {
            data: mileageLogs.map(v => v.conso).reverse(),
            type: "line",
            smooth: true,
            areaStyle: {
              color: "rgba(0, 0, 0, 0.1)"
            },
            lineStyle: {
              width: 1
            }
          }
        ]
      }}
      notMerge={true}
      lazyUpdate={true}
      theme={"theme_name"}
    />
  </div>;
};

const Quotes = ({quotes}) => {
  const {formatAmount} = useFormat();
  const {t} = useTranslation("app");
  const navigate = useNavigate();

  const {featurePortfolioActive, portfolioCurrency} = useSelector(appConfigSelector);

  if (!featurePortfolioActive || Object.values(quotes).length === 0) {
    return null;
  }

  const comparator = (a, b) => {
    if (a.quantity === b.quantity) {
      return a.symbol.localeCompare(b.symbol);
    } else {
      return b.quantity - a.quantity;
    }
  };

  return <div className={clsx("card", styles.card)}>
    <div className={clsx(styles.symbols)}>
      <div className={clsx(styles.symbolContainer, styles.header)}>
        <div className={styles.symbol}>{t("dashboard_symbols_header_code")}</div>
        <div className={styles.gains}>
          <div className={styles.dayGains}>{t("dashboard_symbols_header_day_gains")}</div>
          <div className={styles.ongoingGains}>{t("dashboard_symbols_header_total_gains")}</div>
        </div>
        <div className={styles.priceAndVariation}>
          <div className={styles.price}>{t("dashboard_symbols_header_price")}</div>
          <div className={styles.priceVariation}>{t("dashboard_symbols_header_variation")}</div>
        </div>
      </div>

      {Object.values(quotes).filter(q => q.favorite || q.quantity > 0).toSorted(comparator).map(quote => {
        let priceDiff = quote.price - quote.previousClose;
        let priceClass = null;

        if (priceDiff > 0) {
          priceClass = styles.positive;
        } else if (priceDiff < 0) {
          priceClass = styles.negative;
        }

        let transactionQuantity = " (" + quote.quantity + ")";

        if (quote.quantity === 0) {
          transactionQuantity = null;
        }

        return <div
          key={quote.symbol}
          className={clsx(styles.symbolContainer, "hover-selectable")}
          onClick={() => navigate("/portfolio/" + quote.symbol)}
        >
          <div className={styles.symbol}>
            <div>{quote.symbol}{transactionQuantity}</div>

            <div className={styles.name}>{quote.name}</div>
          </div>

          <div className={styles.gains}>
            <div className={styles.dayGains}>
              <ColoredAmount amount={quote.dayGains}>
                {quote.quantity > 0 ? formatAmount(quote.dayGains, portfolioCurrency) : ""}
              </ColoredAmount>
            </div>

            <div className={styles.ongoingGains}>
              <ColoredAmount amount={quote.ongoingGains}>
                {quote.quantity > 0 ? formatAmount(quote.ongoingGains, portfolioCurrency) : ""}
              </ColoredAmount>
            </div>
          </div>

          <div className={styles.priceAndVariation}>
            <div className={styles.price}>{formatAmount(quote.price, quote.currency)}</div>

            <div className={clsx(styles.priceVariation, priceClass)}>
              <div className={styles.chip}>
                {(priceDiff > 0 ? "+" : "") + ((quote.price / quote.previousClose * 100) - 100).toFixed(2) + " %"}
              </div>
            </div>
          </div>
        </div>;
      })}
    </div>
  </div>;
};

export default Dashboard;
